/**
 * MixView Component
 * Pro Tools-inspired full mixer view with expanded channel strips
 * 
 * Features:
 * - Large channel strips with detailed controls
 * - EQ visualization per channel
 * - Expanded metering
 * - Send/Aux routing section
 * - Master section with detailed controls
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Volume2, 
  VolumeX, 
  Headphones, 
  Sliders,
  Activity,
  Radio,
  Waves,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  GripHorizontal,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { TrackEffectChain } from '../effects/TrackEffectChain';

// Utility: Convert linear volume (0-1) to dB display
const volumeToDb = (volume) => {
  if (volume === 0) return '-∞';
  const db = 20 * Math.log10(volume);
  return db.toFixed(1);
};

/**
 * Large Volume Fader for Mix View
 */
const LargeFader = ({ value = 0.8, onChange, color = 'cyan', label, className }) => {
  const faderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (moveEvent) => {
      if (!faderRef.current) return;
      const rect = faderRef.current.getBoundingClientRect();
      const y = moveEvent.clientY - rect.top;
      const height = rect.height;
      const newValue = Math.max(0, Math.min(1, 1 - (y / height)));
      onChange?.(newValue);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    handleMouseMove(e);
  }, [onChange]);
  
  const percentage = value * 100;
  const colorClasses = {
    cyan: 'from-cyan-600 to-cyan-400',
    amber: 'from-amber-600 to-amber-400',
    green: 'from-green-600 to-green-400',
    purple: 'from-purple-600 to-purple-400',
    pink: 'from-pink-600 to-pink-400',
  };
  
  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      {label && <span className="text-[10px] text-zinc-500 uppercase">{label}</span>}
      <div 
        ref={faderRef}
        className={cn(
          "relative w-10 h-48 bg-zinc-800 rounded cursor-pointer select-none",
          isDragging && "ring-2 ring-cyan-500"
        )}
        onMouseDown={handleMouseDown}
        data-testid="mix-fader"
      >
        {/* Track background */}
        <div className="absolute inset-x-2 inset-y-2 bg-zinc-900 rounded overflow-hidden">
          {/* Filled portion */}
          <motion.div 
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${colorClasses[color] || colorClasses.cyan}`}
            style={{ height: `${percentage}%` }}
            animate={{ height: `${percentage}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
          
          {/* Unity (0dB) marker */}
          <div className="absolute left-0 right-0 h-px bg-white/30" style={{ bottom: '80%' }} />
        </div>
        
        {/* Fader handle */}
        <motion.div 
          className={cn(
            "absolute left-1 right-1 h-6 bg-zinc-200 rounded shadow-lg",
            "border border-zinc-300",
            "flex items-center justify-center",
            isDragging && "bg-white"
          )}
          style={{ bottom: `calc(${percentage}% - 12px)` }}
          animate={{ bottom: `calc(${percentage}% - 12px)` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div className="w-6 h-1 bg-zinc-400 rounded" />
        </motion.div>
        
        {/* dB markers */}
        <div className="absolute -right-8 inset-y-2 flex flex-col justify-between text-[9px] text-zinc-500 font-mono">
          <span>+12</span>
          <span>+6</span>
          <span>0</span>
          <span>-6</span>
          <span>-12</span>
          <span>-24</span>
          <span>-∞</span>
        </div>
      </div>
      
      {/* dB value display */}
      <div className="text-sm font-mono text-white bg-zinc-800 px-2 py-1 rounded">
        {volumeToDb(value)} <span className="text-zinc-500 text-xs">dB</span>
      </div>
    </div>
  );
};

/**
 * Pan Control with visual indicator
 */
const PanControl = ({ value = 0, onChange, className }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const startValue = useRef(0);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startX.current = e.clientX;
    startValue.current = value;
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX.current;
      const sensitivity = 0.005;
      const newValue = Math.max(-1, Math.min(1, startValue.current + deltaX * sensitivity));
      onChange?.(newValue);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [value, onChange]);
  
  const handleDoubleClick = () => onChange?.(0);
  
  const panText = value === 0 ? 'C' : value < 0 ? `L${Math.abs(Math.round(value * 100))}` : `R${Math.round(value * 100)}`;
  const barPosition = ((value + 1) / 2) * 100;
  
  return (
    <div className={cn("flex flex-col items-center gap-1", className)}>
      <span className="text-[10px] text-zinc-500 uppercase">Pan</span>
      
      {/* Pan bar */}
      <div 
        className={cn(
          "relative w-20 h-4 bg-zinc-800 rounded cursor-ew-resize",
          isDragging && "ring-1 ring-cyan-500"
        )}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
      >
        {/* Center line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-600" />
        
        {/* L/R labels */}
        <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">L</span>
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">R</span>
        
        {/* Position indicator */}
        <motion.div 
          className="absolute top-0.5 bottom-0.5 w-2 bg-cyan-500 rounded"
          style={{ left: `calc(${barPosition}% - 4px)` }}
          animate={{ left: `calc(${barPosition}% - 4px)` }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      </div>
      
      <span className="text-[11px] font-mono text-zinc-400">{panText}</span>
    </div>
  );
};

/**
 * Peak Meter with clip indicator
 */
const PeakMeter = ({ level = 0, peakHold = 0, className }) => {
  const percentage = Math.min(100, level * 100);
  const peakPercentage = Math.min(100, peakHold * 100);
  const isClipping = level > 0.95;
  
  return (
    <div className={cn("flex gap-0.5", className)}>
      {/* Left channel */}
      <div className="w-3 h-48 bg-zinc-900 rounded overflow-hidden relative">
        <motion.div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 via-yellow-500 to-red-500"
          style={{ height: `${percentage}%` }}
          animate={{ height: `${percentage}%` }}
          transition={{ duration: 0.05 }}
        />
        {/* Peak hold indicator */}
        <div 
          className="absolute left-0 right-0 h-0.5 bg-white"
          style={{ bottom: `${peakPercentage}%` }}
        />
        {/* Clip indicator */}
        {isClipping && (
          <div className="absolute top-0 left-0 right-0 h-2 bg-red-500 animate-pulse" />
        )}
      </div>
      
      {/* Right channel (slightly offset for stereo effect) */}
      <div className="w-3 h-48 bg-zinc-900 rounded overflow-hidden relative">
        <motion.div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 via-yellow-500 to-red-500"
          style={{ height: `${Math.max(0, percentage * 0.95)}%` }}
          animate={{ height: `${Math.max(0, percentage * 0.95)}%` }}
          transition={{ duration: 0.05 }}
        />
        <div 
          className="absolute left-0 right-0 h-0.5 bg-white"
          style={{ bottom: `${peakPercentage}%` }}
        />
        {isClipping && (
          <div className="absolute top-0 left-0 right-0 h-2 bg-red-500 animate-pulse" />
        )}
      </div>
    </div>
  );
};

/**
 * Mini EQ Display
 */
const MiniEQ = ({ low = 0, mid = 0, high = 0, className }) => {
  const bands = [
    { label: 'L', value: low, color: 'bg-red-500' },
    { label: 'M', value: mid, color: 'bg-yellow-500' },
    { label: 'H', value: high, color: 'bg-green-500' },
  ];
  
  return (
    <div className={cn("flex flex-col items-center gap-1", className)}>
      <span className="text-[10px] text-zinc-500 uppercase">EQ</span>
      <div className="flex gap-1">
        {bands.map(band => (
          <div key={band.label} className="flex flex-col items-center gap-0.5">
            <div className="w-4 h-12 bg-zinc-800 rounded relative overflow-hidden">
              <div 
                className={`absolute bottom-1/2 left-0 right-0 ${band.color}`}
                style={{ 
                  height: `${Math.abs(band.value) * 50}%`,
                  bottom: band.value >= 0 ? '50%' : 'auto',
                  top: band.value < 0 ? '50%' : 'auto',
                }}
              />
              <div className="absolute left-0 right-0 top-1/2 h-px bg-zinc-600" />
            </div>
            <span className="text-[8px] text-zinc-500">{band.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Large Channel Strip for Mix View
 */
const LargeChannelStrip = ({
  track,
  index,
  isSelected,
  onSelect,
  onVolumeChange,
  onPanChange,
  onMuteToggle,
  onSoloToggle,
  onNameChange,
  peakLevel = 0,
  // Effect chain props
  onAddEffect,
  onRemoveEffect,
  onToggleEffect,
  onReorderEffects,
  onEffectParamChange,
  // Drag & drop props
  isDragged,
  isDragOver,
  dropPosition,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(track?.name || `Track ${index + 1}`);
  const [effectsExpanded, setEffectsExpanded] = useState(false);
  
  const handleNameSubmit = () => {
    setIsEditing(false);
    if (editName.trim() && editName !== track?.name) {
      onNameChange?.(editName.trim());
    }
  };
  
  const trackColors = {
    cyan: { border: 'border-cyan-500/50', bg: 'bg-cyan-500/5', fader: 'cyan' },
    indigo: { border: 'border-indigo-500/50', bg: 'bg-indigo-500/5', fader: 'purple' },
    purple: { border: 'border-purple-500/50', bg: 'bg-purple-500/5', fader: 'purple' },
    pink: { border: 'border-pink-500/50', bg: 'bg-pink-500/5', fader: 'pink' },
    amber: { border: 'border-amber-500/50', bg: 'bg-amber-500/5', fader: 'amber' },
    green: { border: 'border-green-500/50', bg: 'bg-green-500/5', fader: 'green' },
  };
  
  const colorKey = track?.color?.includes('cyan') ? 'cyan' 
    : track?.color?.includes('indigo') ? 'indigo'
    : track?.color?.includes('purple') ? 'purple'
    : track?.color?.includes('pink') ? 'pink'
    : track?.color?.includes('amber') ? 'amber'
    : track?.color?.includes('green') ? 'green'
    : 'cyan';
  
  const colors = trackColors[colorKey];
  
  // Drag styles
  const dragClassName = isDragged 
    ? 'opacity-50 scale-95' 
    : isDragOver 
      ? `${dropPosition === 'before' ? 'border-l-4 border-l-cyan-500' : 'border-r-4 border-r-cyan-500'} bg-cyan-500/10` 
      : '';
  
  return (
    <motion.div
      className={cn(
        "flex flex-col items-center p-4 rounded-xl border backdrop-blur-sm transition-all min-w-[120px]",
        colors.border,
        colors.bg,
        isSelected && "ring-2 ring-cyan-500",
        dragClassName
      )}
      onClick={() => onSelect?.()}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      data-testid={`mix-channel-${track?.id || index}`}
      draggable
      onDragStart={(e) => onDragStart?.(e, track?.id, index)}
      onDragOver={(e) => {
        e.preventDefault();
        // For horizontal layout, use left/right instead of top/bottom
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        const position = e.clientX < midpoint ? 'before' : 'after';
        onDragOver?.(e, track?.id, index, position);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop?.(e, track?.id, index)}
      onDragEnd={onDragEnd}
    >
      {/* Drag Handle */}
      <div 
        className="absolute top-1 left-1/2 -translate-x-1/2 text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing transition-colors"
        title="Drag to reorder"
      >
        <GripHorizontal className="w-4 h-4" />
      </div>
      
      {/* Track number */}
      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center mb-3">
        <span className="text-sm font-bold text-zinc-300">{index + 1}</span>
      </div>
      
      {/* Track name */}
      <div 
        className="w-full text-center mb-3 cursor-pointer"
        onDoubleClick={() => setIsEditing(true)}
      >
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
            className="w-full px-2 py-1 text-sm bg-zinc-800 border border-cyan-500 rounded text-center text-white outline-none"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="text-sm font-medium text-zinc-200 truncate block">
            {track?.name || `Track ${index + 1}`}
          </span>
        )}
      </div>
      
      {/* Type indicator */}
      <span className="text-[10px] uppercase text-zinc-500 mb-3">
        {track?.type === 'instrument' ? track.instrumentType || 'INST' : 'AUDIO'}
      </span>
      
      {/* Mini EQ */}
      <MiniEQ 
        low={track?.eq?.low || 0} 
        mid={track?.eq?.mid || 0} 
        high={track?.eq?.high || 0}
        className="mb-3"
      />
      
      {/* Pan control */}
      <PanControl 
        value={track?.pan || 0} 
        onChange={onPanChange}
        className="mb-3"
      />
      
      {/* Mute / Solo */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={(e) => { e.stopPropagation(); onMuteToggle?.(); }}
          className={cn(
            "w-10 h-8 rounded font-bold text-xs transition-all",
            track?.muted 
              ? "bg-red-500 text-white shadow-lg shadow-red-500/30" 
              : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
          )}
          data-testid={`mix-mute-${track?.id || index}`}
        >
          M
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onSoloToggle?.(); }}
          className={cn(
            "w-10 h-8 rounded font-bold text-xs transition-all",
            track?.solo 
              ? "bg-amber-500 text-white shadow-lg shadow-amber-500/30" 
              : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
          )}
          data-testid={`mix-solo-${track?.id || index}`}
        >
          S
        </button>
      </div>
      
      {/* Effect Chain */}
      <div className="w-full mb-3 px-1" onClick={(e) => e.stopPropagation()}>
        <TrackEffectChain
          trackId={track?.id}
          effects={track?.effectChain || []}
          onAddEffect={onAddEffect}
          onRemoveEffect={onRemoveEffect}
          onToggleEffect={onToggleEffect}
          onReorderEffects={onReorderEffects}
          onParamChange={onEffectParamChange}
          isExpanded={effectsExpanded}
          onToggleExpand={() => setEffectsExpanded(!effectsExpanded)}
        />
      </div>
      
      {/* Fader and meter */}
      <div className="flex gap-2">
        <PeakMeter level={peakLevel} peakHold={peakLevel * 0.9} />
        <LargeFader 
          value={track?.volume ?? 0.8} 
          onChange={onVolumeChange}
          color={colors.fader}
        />
      </div>
    </motion.div>
  );
};

/**
 * Master Channel Strip
 */
const MasterStrip = ({ volume = 0.8, onVolumeChange }) => {
  return (
    <motion.div
      className="flex flex-col items-center p-4 rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent backdrop-blur-sm min-w-[140px]"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      data-testid="mix-master-strip"
    >
      {/* Master label */}
      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
        <Volume2 className="w-5 h-5 text-amber-500" />
      </div>
      
      <span className="text-sm font-bold text-amber-500 uppercase mb-3">Master</span>
      
      <span className="text-[10px] uppercase text-zinc-500 mb-3">Output</span>
      
      {/* Stereo meter */}
      <div className="flex gap-1 mb-3">
        <div className="w-4 h-32 bg-zinc-900 rounded relative overflow-hidden">
          <div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 via-yellow-500 to-red-500"
            style={{ height: `${volume * 80}%` }}
          />
        </div>
        <div className="w-4 h-32 bg-zinc-900 rounded relative overflow-hidden">
          <div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 via-yellow-500 to-red-500"
            style={{ height: `${volume * 78}%` }}
          />
        </div>
      </div>
      
      {/* Fader */}
      <LargeFader 
        value={volume} 
        onChange={onVolumeChange}
        color="amber"
      />
    </motion.div>
  );
};

/**
 * Main MixView Component
 */
export const MixView = ({
  tracks = [],
  masterVolume = 0.8,
  selectedTrackId,
  onTrackVolumeChange,
  onTrackPanChange,
  onTrackMuteToggle,
  onTrackSoloToggle,
  onTrackSelect,
  onTrackNameChange,
  onMasterVolumeChange,
  // Effect chain handlers
  onAddEffect,
  onRemoveEffect,
  onToggleEffect,
  onReorderEffects,
  onEffectParamChange,
  peakLevels = {},
  // Drag & drop handlers
  draggedTrackId,
  dragOverTrackId,
  dropPosition,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  className,
}) => {
  return (
    <div className={cn("flex-1 overflow-hidden flex flex-col", className)} data-testid="mix-view">
      {/* Header */}
      <div className="h-12 bg-zinc-900/50 backdrop-blur-sm border-b border-zinc-800/50 px-6 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Sliders className="w-5 h-5 text-cyan-500" />
          <span className="text-sm font-medium text-zinc-300">Mix View</span>
          <span className="text-xs text-zinc-500">({tracks.length} channels)</span>
        </div>
        
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Activity className="w-3 h-3" />
            Stereo Out
          </span>
        </div>
      </div>
      
      {/* Mixer content */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-6">
        <div className="flex gap-3 h-full items-start min-w-max">
          {/* Track channel strips */}
          {tracks.map((track, index) => (
            <LargeChannelStrip
              key={track.id}
              track={track}
              index={index}
              isSelected={track.id === selectedTrackId}
              onSelect={() => onTrackSelect?.(track.id)}
              onVolumeChange={(value) => onTrackVolumeChange?.(track.id, value)}
              onPanChange={(value) => onTrackPanChange?.(track.id, value)}
              onMuteToggle={() => onTrackMuteToggle?.(track.id)}
              onSoloToggle={() => onTrackSoloToggle?.(track.id)}
              onNameChange={(name) => onTrackNameChange?.(track.id, name)}
              peakLevel={peakLevels[track.id] || 0}
              onAddEffect={onAddEffect}
              onRemoveEffect={onRemoveEffect}
              onToggleEffect={onToggleEffect}
              onReorderEffects={onReorderEffects}
              onEffectParamChange={onEffectParamChange}
              // Drag & drop props
              isDragged={draggedTrackId === track.id}
              isDragOver={dragOverTrackId === track.id}
              dropPosition={dragOverTrackId === track.id ? dropPosition : null}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          ))}
          
          {/* Separator */}
          {tracks.length > 0 && (
            <div className="w-px h-full bg-zinc-700 mx-4 self-stretch" />
          )}
          
          {/* Master strip */}
          <MasterStrip 
            volume={masterVolume}
            onVolumeChange={onMasterVolumeChange}
          />
        </div>
        
        {/* Empty state */}
        {tracks.length === 0 && (
          <div className="flex items-center justify-center h-full text-zinc-500">
            <div className="text-center">
              <Sliders className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No tracks to mix</p>
              <p className="text-sm mt-1">Add tracks in Edit view to see them here</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MixView;
